import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ModalRenderer from '@components/Modals/ModalRenderer';
import LocalAppContextProvider from '@context/LocalAppContext';
import { UserProvider } from '@context/UserContext';
import FirebaseAuthGuard from '@domain/App/FirebaseAuthGuard';
import ErrorPage from '@domain/Errors/components/ErrorPage';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import initFirebase from '@helpers/initFirebase';
import queryClient from '@helpers/initQueryClient';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import RepeatTheme from '@themes/RepeatTheme';
import { FlagsProvider } from 'flagged';
import { Suspense } from 'react';
import { QueryClientProvider } from 'react-query';
import { BrowserRouter } from 'react-router-dom';
import { TssCacheProvider } from 'tss-react';
import PlatformSelectAnimationProvider from '@context/PlatformSelectAnimationContext';
import PlatformSelectAnimation from '@domain/Platform/components/PlatformSelectAnimation';
import RepeatAppScreen from './RepeatAppScreen';
import forcePolyfill from './forcePolyfill';
import './styles/global.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'simplebar-react/dist/simplebar.min.css';
import './styles/cookieconsent.min.css';
import { createRoot } from 'react-dom/client';
import RepeatAppScreenWithdrawalOnly from './RepeatAppScreenWithdrawalOnly';
// Added here to force injection of an Object.fromEntries polyfill
// as this method is used by `flagged`, but it doesn't include the
// polyfill in the bundle.
forcePolyfill();
Sentry.init({
    enabled: process.env.SENTRY_ENV !== 'dev',
    release: process.env.BITBUCKET_COMMIT || process.env.REPEAT_RELEASE,
    dsn: 'https://bec1af7aab7b45b6af8850880edb9102@o470953.ingest.sentry.io/5502314',
    integrations: [Sentry.browserTracingIntegration(), Sentry.extraErrorDataIntegration()],
    environment: process.env.SENTRY_ENV,
    // 25% chance of any error sending through the entire application trace.
    // See https://docs.sentry.io/product/performance/distributed-tracing/ for more details
    tracesSampleRate: 0.25,
    normalizeDepth: 4,
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed with undelivered notifications.'],
    denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Indicative
        /api.indicative.com\//i,
        // Localize Library
        /localizecdn.com\//i,
    ],
});
initFirebase();
const muiCache = createCache({
    key: 'mui',
    prepend: true,
});
const tssCache = createCache({
    key: 'tss',
});
const container = document.getElementById('repeat-app');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container);
root.render(_jsx(FlagsProvider, { features: window.feature_flags, children: _jsx(LocalAppContextProvider, { children: _jsx(QueryClientProvider, { client: queryClient, children: _jsx(BrowserRouter, { children: _jsx(CacheProvider, { value: muiCache, children: _jsx(TssCacheProvider, { value: tssCache, children: _jsx(ThemeProvider, { theme: RepeatTheme, children: _jsx(FirebaseAuthGuard, { children: _jsx(Sentry.ErrorBoundary, { fallback: ({ error }) => _jsx(ErrorPage, { error: error }), children: _jsx(Suspense, { fallback: _jsx("div", {}), children: _jsxs(UserProvider, { children: [_jsxs(PlatformSelectAnimationProvider, { children: [window.feature_flags.includes('semisonic_phase_2') ? (_jsx(RepeatAppScreenWithdrawalOnly, {})) : (_jsx(RepeatAppScreen, {})), _jsx(PlatformSelectAnimation, {})] }), _jsx(ModalRenderer, {})] }) }) }) }) }) }) }) }) }) }) }));
